import { LitElement } from '~/lib/bn-lit-element';

import {
  LitEntity,
  SirenActionMixin,
  html,
  css,
  renderIf,
} from '../utility/lit-entity';
import { LitSirenEntityMixin } from '../utility/lit-siren-entity-mixin';
import { sharedStyles } from '../styles/shared-styles';
import './bn-flash';
import './bn-placeholder';
import './bn-form-field';

const BnActionMixin = (superClass) => class extends superClass {
  constructor() {
    super();
    this.errortext = '';
    this.submitting = false;
    this.buttonstyle = 'contained';
  }

  static get properties() {
    return {
      actionname: { type: String },
      errortext: { type: String },
      successtext: { type: String },
      submittext: { type: String },
      buttonstyle: { type: String },
      buttonicon: { type: String },
      suppressflash: { type: Boolean },
      suppressheader: { type: Boolean },
      disabled: { type: Boolean },
      showdisabled: { type: Boolean },
      submitting: { type: Boolean, attribute: false },
      highlightErrors: { type: Boolean, attribute: 'highlight-errors' },
      invalidFields: { type: Boolean, attribute: 'invalid-fields', reflect: true },
      noContent: { type: Boolean, attribute: 'no-content', reflect: true },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      :host([no-content]) {
        display: none;
      }
      .no-action {
        font-weight: bold;
        text-align: center;
        padding: 10px;
      }
      form {
        margin: 0 0.25rem;
        display: var(--form-display, block);
        flex-wrap: wrap;
      }
      bn-form-field {
        width: var(--form-field-width, 100%);
      }
      .form-actions {
        width: 100%;
      }
      .bn-button-container {
        display: flex;
        gap: 0.5rem;
      }
    `];
  }

  render() {
    const action = !this.loading && this.actionname && this.entity?.getActionByName(this.actionname);

    if (this.noContent) {
      return html``;
    }

    this.preRender(action);
    const ready = action && !this.invalidFields && !this.submitting;
    const title = action && (action.title || action.name);

    return html`
      ${renderIf(!this.suppressheader && title, () => html`<slot name="header"><h3>${title}</h3></slot>`)}
      ${renderIf(!this.suppressFlash, html`<bn-flash id="status"></bn-flash>`)}
      <form @submit="${(e) => this._submit(e, action)}">
        ${renderIf(action && action.fields, () => action.fields.map(field => this.renderField(field)))}
        <div class="form-actions">
          <button
            type="submit"
            class="button button--${this.buttonstyle}"
            @click="${this._onClick}"
            ?disabled="${this.disabled || !ready}"
          >
            <slot name="submit-content" class="bn-button-container">
              ${renderIf(this.buttonicon, () => html`<bn-icon name="fal ${this.buttonicon}"></bn-icon>`)}
              ${this.submittext || 'Submit'}
            </slot>
          </button>
        </div>
      </form>
    `;
  }

  renderField(field) {
    return html`
      <bn-form-field
        ?checked="${field.checked}"
        ?required="${field.class && field.class.includes('required')}"
        .cols="${field.cols}"
        .max="${field.max}"
        .min="${field.min}"
        .name="${field.name}"
        .placeholder="${field.placeholder}"
        .rows="${field.rows}"
        .title="${field.title}"
        .type="${field.type}"
        .value="${field.value}"
        .options="${field.options}"
        ?require-option="${field.class && field.class.includes('require-option')}"
        ?highlight-errors="${this.highlightErrors}"
        @form-data="${this.onFormData}"
        autocomplete="${field.autocomplete}"
      >
        ${renderIf(field.suffix, html`<span slot="suffix" class="suffix">${field.suffix}</span>`)}
      </bn-form-field>
    `;
  }

  onFormData(e) {
    this._checkMissingFields(e);
  }

  firstUpdated() {
    this._checkMissingFields();
  }

  _entityChanged(entity) {
    super._entityChanged(entity);

    const action = !this.loading && this.actionname && this.entity?.getActionByName(this.actionname);
    this.noContent = !(action || this.showdisabled);

    const flash = this.shadowRoot.querySelector('#status');
    flash && flash.clear();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  preRender(action /* SirenAction */) {
    // overwrite in child class to process fields before render
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  preSubmit(fields /* FormData|URLSearchParams */) {
    // overwrite in child class to process fields before submission
  }

  _getFlash() {
    const flash = this.shadowRoot.querySelector('#status');
    if (!flash || this.suppressflash) {
      return {
        clear: () => {},
        success: () => {},
        critical: () => {},
      };
    }
    return flash;
  }

  async _submit(e) {
    e.preventDefault();
    if (this.invalidFields) {
      this.highlightErrors = true;
      return;
    }

    return this.submit();
  }

  async submit() {
    if (this.submitting) {
      return;
    }

    const action = this.entity.getActionByName(this.actionname);
    const flash = this._getFlash();
    flash.clear();

    const result = {};
    try {
      if (!action) {
        throw new Error(`Could not find [${this.actionname}] action`);
      }
      const fields = this.getActionFields(action);
      this.shadowRoot.querySelectorAll('bn-form-field').forEach((field) => fields.set(field.name, field.input.value));
      const continueSubmit = this.preSubmit(fields);
      if (continueSubmit === false) {
        return;
      }

      this.submitting = true;
      this.dispatchEvent(new CustomEvent('submit', { bubbles: true, composed: true }));
      const { entity, response, error } = await this.submitAction(action, fields);
      if (error) {
        throw new Error(error);
      }
      result.entity = entity;
      result.response = response;
      flash.success(this.successtext || 'Success.');
      this.dispatchEvent(new CustomEvent('response', { detail: { entity, response }, bubbles: true, composed: true }));
    } catch (error) {
      result.error = error;
      flash.critical(html`${this.errortext}<div><code>${error.message}</code></div>`);
      this.dispatchEvent(new ErrorEvent('error', { error, bubbles: true, composed: true }));
    } finally {
      this.submitting = false;
    }
    return result;
  }

  _checkMissingFields() {
    const formFields = this.shadowRoot.querySelectorAll('bn-form-field');
    for (const field of formFields) {
      if (field.currentError) {
        this.invalidFields = true;
        return;
      }
    }

    this.invalidFields = false;
  }
};

export class BnAction extends BnActionMixin(SirenActionMixin(LitEntity)) {}
window.customElements.define('bn-action', BnAction);

export class BnSubAction extends BnActionMixin(SirenActionMixin(LitSirenEntityMixin(LitElement))) {}
window.customElements.define('bn-sub-action', BnSubAction);
